import './scss/default.scss';

// import * as Sentry from "@sentry/react"; // Temporarily disabled for build test

import { Outlet, useOutletContext } from "react-router";
import React, { createContext, useEffect, useState } from 'react';
import {
  RouterProvider,
  createBrowserRouter
} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { gql, useQuery } from '@apollo/client';

import { ApolloProvider } from '@apollo/client';
import Client from './js/utility/apolloEnvironment';
import Favorites from './js/pages/Favorites.jsx';
import Feedback from './js/pages/Feedback.jsx';
import Home from './js/pages/Home.jsx';
import Hours from './js/pages/Hours.jsx';
import Item from './js/pages/Item.jsx';
import Location from './js/pages/Location.jsx';
import NotFound from './js/pages/NotFound.jsx';
import Settings from './js/pages/Filtering.jsx';
import Upcoming from './js/pages/Upcoming.jsx';
import common from './js/utility/common.js';
import { createRoot } from 'react-dom/client';
import purdueMuiTheme from './js/styles/baseThemes/purdueTheme';

const GET_USER = gql`
  query getCurrentUser {
    currentUser {
      commonName
      firstName
      lastName
      email
      favorites {
        dateAdded
        favoriteId
        item {
          id
          itemId
          name
          isNutritionReady
          isDiscontinued
          appearances {
            date
            locationName
            mealName
            stationName
          }
          components {
            itemId
            name
          }
          traits {
            name
            svgIcon
            svgIconWithoutBackground
          }
        }
      }
    }
  }
`;

const GET_LOCATIONS = gql`
  query getStartLocations {
    diningCourtCategories {
      name
      diningCourts {
        id
        category
        name
        formalName
        lineLength
        logoUrl
        upcomingMeals {
          id
          name
          type
          startTime
          endTime
        }
      }
    }
  }
`;

window.basePath = "/menus";
let environment = process.env.NODE_ENV === 'development' ? '/' : '/menus/';
const AppContext = createContext();

const router = createBrowserRouter([
  {
    element: <MainLayout />,
    children: [
      { path: '/', element: <Home AppContext={AppContext} />, handle: { pageName: "home", displayMenu: false }, errorElement: <NotFound AppContext={AppContext} /> },
      { path: '/index.html', element: <Home AppContext={AppContext} />, handle: { pageName: "home", displayMenu: false }, errorElement: <NotFound AppContext={AppContext} /> },
      { path: 'hours', element: <Hours AppContext={AppContext} />, handle: { pageName: "hours", displayMenu: false } },
      { path: 'item/:itemId/:itemMenuId?', element: <Item AppContext={AppContext} />, handle: { pageName: "item", displayMenu: false } },
      { path: 'settings', element: <Settings AppContext={AppContext} />, handle: { pageName: "settings", displayMenu: false } },
      {
        path: 'favorites', element: <Favorites AppContext={AppContext} />, handle: { pageName: "favorites", displayMenu: false }
      },
      { path: 'favorites/upcoming', element: <Upcoming AppContext={AppContext} />, handle: { pageName: "upcoming favorites", displayMenu: false } },
      { path: ':locationName/:year?/:month?/:day?/:meal?/', element: <Location AppContext={AppContext} />, handle: { pageName: "location", displayMenu: true }, errorElement: <NotFound AppContext={AppContext} /> },
      { path: 'feedback', element: <Feedback AppContext={AppContext} />, handle: { pageName: "feedback", displayMenu: false } },
      { path: '*', element: <NotFound AppContext={AppContext} />, handle: { pageName: "not found", displayMenu: false } }
    ]
  },
], {
  basename: environment
})

export function useAppState() {
  return useOutletContext()
}

export function MainLayout() {
  const [locations, setLocations] = useState();
  const [currentUser, setCurrentUser] = useState();
  const getLocationsQuery = useQuery(GET_LOCATIONS);

  const currentUserQuery = useQuery(GET_USER);

  useEffect(() => {
    if (!currentUserQuery.loading && currentUserQuery.data.currentUser !== null) {
      setCurrentUser(currentUserQuery.data.currentUser);
    } else {
      setCurrentUser("Guest");
    }

    if (!getLocationsQuery.loading) {
      setLocations(getLocationsQuery.data.diningCourtCategories)
    }
  }, [currentUserQuery, getLocationsQuery])

  if (currentUserQuery.loading || currentUser === undefined || locations === undefined) {
    return <div className="loading">
      <div className="circle">
        <div className="circle">
          <div className="circle">
            <div className="circle-center">
              <div className="circle-center__inner"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="loading-text">Loading</div>
    </div>
  } else {
    return <ThemeProvider theme={createTheme(purdueMuiTheme)}>
      <Outlet context={{locations, currentUser, GET_USER}} />
    </ThemeProvider>
  }
}

export default function App() {
  return <ApolloProvider client={Client}><RouterProvider router={router} /></ApolloProvider>
}

/* Temporarily disabled for build test\nSentry.init({
  dsn: "https://606fc70bfbd551cf1345c2d9b0f1f9a5@o1383521.ingest.sentry.io/4506753060700160",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Environment
  environment: common.tier,
  // Performance Monitoring
  tracesSampleRate: .01, //  Capture 100% of the transactions
  sampleRate: 0.01,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /\.api\.hfs\.purdue\.edu\/menus/],
  // Session Replay
  replaysSessionSampleRate: 0.01, // This sets the sample rate at 1%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});\n*/

const domRoot = createRoot(document.getElementById('app'));
domRoot.render(<App />
);